/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { TransactionListItem } from '@root/entities/transaction/ui'
import { useBreakpoint } from '@root/shared'
import { FadeOverflowVirtuozo } from '@root/shared/ui-kit/utils/fade-overflow-virtuozo'

import { reatomUserTransactions } from '../index'

import { TransactionHistoryListError } from './transaction-history-list-error'
import { TransactionHistoryListLoading } from './transaction-history-list-loading'

import type { Transaction } from '@root/entities/transaction'
import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'
import type { InfoMessage } from '@root/shared/ui-kit'

export type TransactionHistoryListProps = {
  onOpenTransactionDetails?: (transaction: Transaction.Transaction) => void
  className?: string
  delugeId?: DelugeId
  model?: ReturnType<typeof reatomUserTransactions>
  renderEmptyMessage: () => React.ReactElement<typeof InfoMessage>
  variant?: 'light' | 'dark'
  withWallet?: boolean
} & ClassNameProps

export const TransactionHistoryList = ({
  onOpenTransactionDetails,
  className,
  delugeId,
  model,
  renderEmptyMessage,
  variant = 'dark',
  withWallet = false,
}: TransactionHistoryListProps) => {
  const breakpoint = useBreakpoint()
  const [network] = useAtom(selectedNetworkAtom)
  const transactionsStateAtom = useMemo(() => {
    return (
      model ??
      reatomUserTransactions({
        delugeId,
        network,
        pairAddress: undefined,
      })
    )
  }, [delugeId, model, network])

  const [transactionsState] = useAtom(transactionsStateAtom)

  const historyState = useMemo(() => {
    return transactionsState.pipe(AsyncStateN.map((it) => it.data))
  }, [transactionsState])

  const [hasMoreItems] = useAtom(transactionsStateAtom.hasMore)
  const [loadMoreState] = useAtom(transactionsStateAtom.loadMoreState)
  const load = useAction(transactionsStateAtom.run)
  const loadMore = useAction(transactionsStateAtom.loadMore)

  const withBackground = breakpoint === 'desktop'

  const handleOpenTransactionDetails = (tx: Transaction.Transaction) => {
    onOpenTransactionDetails?.(tx)
  }

  const handleLoadMore = () => {
    if (AsyncStateN.isError(loadMoreState)) {
      return
    }

    loadMore()
  }

  if (AsyncStateN.isError(historyState)) {
    return (
      <TransactionHistoryListError
        onRetry={load}
        withBackground={withBackground}
      />
    )
  }

  const renderRow = (_index: number, tx: Transaction.Transaction) => (
    <TransactionListItem
      tx={tx}
      onClick={handleOpenTransactionDetails}
      css={css`
        margin: 0 6px 8px;
      `}
      withWallet={withWallet}
    />
  )

  return (
    <FadeOverflowVirtuozo
      className={className}
      blockStart={16}
      blockEnd={16}
      data={historyState.value ?? []}
      itemContent={renderRow}
      components={{
        Footer: () => {
          if (AsyncStateN.isError(loadMoreState)) {
            return (
              <TransactionHistoryListError
                onRetry={loadMore}
                withBackground={withBackground}
              />
            )
          }

          if (hasMoreItems && AsyncStateN.isSuccess(historyState)) {
            return <TransactionHistoryListLoading withWallet={withWallet} />
          }
        },
        EmptyPlaceholder: () => {
          const isEmpty = historyState.value === undefined

          if (isEmpty && AsyncStateN.isLoading(historyState)) {
            return <TransactionHistoryListLoading withWallet={withWallet} />
          }

          return renderEmptyMessage()
        },
      }}
      endReached={handleLoadMore}
      increaseViewportBy={300}
      data-empty={!hasMoreItems}
      css={css`
        ${variant === 'light' &&
        css`
          --tx-background-color: var(--backgroundA-3);
          --tx-background-color-hover: var(--background-2);
          --tx-separator-color: var(--backgroundA-2);
          --tx-separator-color-hover: var(--background-3);
        `}
        ${variant === 'dark' &&
        css`
          --tx-background-color: var(--appearanceA-5);
          --tx-background-color-hover: var(--backgroundA-2);
          --tx-separator-color: var(--backgroundA-2);
          --tx-separator-color-hover: var(--backgroundA-3);
        `}
      `}
    />
  )
}
